import './QuestionTrueFalseEditAnswer.scss';

import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { useDispatch } from 'react-redux';

import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';

import { toggleAnswerCorrect } from '../../slices/quiz/quizSlice';
import { useTrueFalseValue } from './hooks';

interface Props {
  answerIds: Array<string | number>;
  isDisabled: boolean;
}

const QuestionTrueFalseEditAnswer: React.FC<Props> = ({
  answerIds,
  isDisabled,
}: React.PropsWithChildren<Props>) => {
  const { value, trueAnswer, falseAnswer } = useTrueFalseValue({
    answerIds,
    isEditing: true,
  });
  const dispatch = useDispatch();
  const toggleClasses = cx(
    'flex flex-1 items-center justify-center px-8 py-2 rounded-2',
    'font-sans text-base leading-8',
    'transition ease-in-out duration-100'
  );
  const trueClasses = React.useMemo(
    () =>
      cx(toggleClasses, {
        'text-white font-600 bg-green-500 hover:bg-green-600': value === true,
        'text-grey-700 bg-white hover:text-green-600 hover:bg-green-100':
          value !== true,
      }),
    [toggleClasses, value]
  );

  const falseClasses = React.useMemo(
    () =>
      cx(toggleClasses, {
        'text-white font-600 bg-red-500 hover:bg-red-600': value === false,
        'text-grey-700 bg-white hover:text-red-600 hover:bg-red-100':
          value !== false,
      }),
    [toggleClasses, value]
  );

  const handleRadioChange = React.useCallback(() => {
    [trueAnswer, falseAnswer].forEach((answer) =>
      dispatch(toggleAnswerCorrect({ answerId: answer.id }))
    );
  }, [dispatch, trueAnswer, falseAnswer]);

  return (
    <>
      <label className="relative flex mr-4 rounded-2 cursor-pointer truefalse-option">
        <FocusIndicator />
        <input
          type="radio"
          name="trueOrFalse"
          value="true"
          className="sr-only"
          checked={value == true}
          onChange={handleRadioChange}
          disabled={isDisabled}
        />
        <span className={trueClasses}>{translate('True')}</span>
      </label>
      <label className="relative flex rounded-2 cursor-pointer truefalse-option">
        <FocusIndicator />
        <input
          type="radio"
          name="trueOrFalse"
          value="false"
          className="sr-only"
          checked={value == false}
          onChange={handleRadioChange}
          disabled={isDisabled}
        />
        <span className={falseClasses}>{translate('False')}</span>
      </label>
    </>
  );
};

export default QuestionTrueFalseEditAnswer;
